import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Wifi, Coffee, Building2, Shield, Sparkles, Clock, Users, CheckCircle2, Star, Calendar, MessageCircle, Quote, ImageIcon, Briefcase } from "lucide-react";
import { api, formatBRL, formatApiErrorDetail } from "../../lib/api";
import { maskPhoneBR, sanitizePhone } from "../../lib/phone";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// Resolve a photo URL: accepts absolute http(s), data URIs, or "/api/uploads/..." paths
const resolvePhoto = (p) => {
  if (!p) return null;
  if (typeof p !== "string") return null;
  if (p.startsWith("http://") || p.startsWith("https://") || p.startsWith("data:")) return p;
  if (p.startsWith("/")) return `${BACKEND}${p}`;
  return p;
};

// Descriptions for each space category — used in the Ambientes section
const CATEGORY_COPY = {
  "Escritório Executivo": "Salas privativas para quem precisa de foco total, privacidade e endereço fixo.",
  "Escritórios Executivos": "Salas privativas para quem precisa de foco total, privacidade e endereço fixo.",
  "Coworking": "Estações compartilhadas em ambiente colaborativo, ideal para freelas e profissionais autônomos.",
  "Sala de Reunião": "Espaços equipados para reuniões presenciais ou videoconferência com clientes e equipes.",
  "Consultório": "Sala preparada para atendimentos individuais com discrição e conforto.",
};

export default function Home() {
  const [plans, setPlans] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [tour, setTour] = useState({ name: "", phone: "", email: "" });
  const [tourLoading, setTourLoading] = useState(false);

  useEffect(() => {
    api.get("/public/plans").then(({ data }) => setPlans(data)).catch(() => {});
    api.get("/public/spaces").then(({ data }) => setSpaces(data)).catch(() => {});
  }, []);

  // Group spaces by category — only rentable spaces (hourly_rate > 0); institutional items excluded
  const ambientes = useMemo(() => {
    const map = new Map();
    for (const s of spaces) {
      const rate = Number(s.hourly_rate || 0);
      if (rate <= 0) continue;
      let cat = s.category || "Outros";
      if (cat.toLowerCase().includes("escrit")) cat = "Escritórios Executivos";
      if (!map.has(cat)) {
        map.set(cat, { category: cat, count: 0, sample: s, photo: null, minRate: rate });
      }
      const entry = map.get(cat);
      entry.count += 1;
      entry.minRate = Math.min(entry.minRate, rate);
      // Pick the first available photo across the group
      if (!entry.photo) {
        const firstPhoto = (s.photos || []).find(Boolean);
        if (firstPhoto) entry.photo = resolvePhoto(firstPhoto);
      }
    }
    return Array.from(map.values());
  }, [spaces]);

  const submitTour = async (e) => {
    e.preventDefault();
    setTourLoading(true);
    try {
      await api.post("/public/tour-lead", {
        ...tour,
        phone: sanitizePhone(tour.phone),
        source: "hero_form",
        interest: "Tour Gratuito",
      });
      toast.success("Recebemos! Entraremos em contato no seu WhatsApp.");
      setTour({ name: "", phone: "", email: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao enviar");
    } finally {
      setTourLoading(false);
    }
  };

  const features = [
    { icon: Wifi, title: "Internet dedicada", desc: "Conectividade fibra de alta performance" },
    { icon: Coffee, title: "Café & lounge premium", desc: "Cafés especiais e bebidas inclusas" },
    { icon: Building2, title: "Salas de reunião", desc: "Equipadas com videoconferência" },
    { icon: Shield, title: "Segunda a sábado", desc: "Funcionamento durante toda a semana útil" },
    { icon: Sparkles, title: "Endereço fiscal", desc: "Receba correspondências e formalize sua empresa" },
    { icon: Clock, title: "Recepção dedicada", desc: "Atendimento profissional aos seus clientes" },
  ];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1497366412874-3415097a27e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwY293b3JraW5nJTIwc3BhY2V8ZW58MHx8fHwxNzc4NTc1Mzk2fDA&ixlib=rb-4.1.0&q=85"
            alt="Coworking premium VM Corporate"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1128]/95 via-[#0A1128]/85 to-[#0A1128]/50" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#D4AF37] mb-8 shadow-xl shadow-[#D4AF37]/30" data-testid="hero-badge">
              <Sparkles size={14} className="text-[#0A1128]" />
              <span className="text-sm font-bold text-[#0A1128] tracking-wide">A partir de R$ 22,00 / hora</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-[1.08] tracking-tight">
              VM Corporate —
              <span className="block mt-2 italic font-display text-[#D4AF37]">Coworking &amp; Escritórios no Centro de Piabetá.</span>
            </h1>
            <p className="mt-6 text-xl text-white/85 font-semibold">
              Alugue por hora, turno ou mês. Pague só pelo tempo que utilizar.
            </p>
            <p className="mt-4 text-base text-white/65 leading-relaxed max-w-2xl">
              Chegue apenas com seu notebook ou material de atendimento. Nós entregamos recepção VIP,
              internet dedicada ultraveloz, ar-condicionado, café e endereço comercial.
              Sem fiador, sem obra e sem contrato longo.
            </p>

            <div className="mt-10 max-w-xl space-y-3" data-testid="hero-hub">
              {[
                { icon: Building2, title: "Nossos Preços & Salas", sub: "Valores por hora de cada ambiente", href: "#precos", gold: false },
                { icon: Briefcase, title: "Escritório Virtual (CNPJ)", sub: "Endereço fiscal e comercial para sua empresa", href: "#escritorio-virtual", gold: false },
                { icon: Calendar, title: "Planos & Pacotes", sub: "Banco de Horas com até 25% de desconto", href: "/planos", gold: false, route: true },
                { icon: Sparkles, title: "Como Funciona", sub: "Entenda o VM Corporate em 3 passos", href: "#como-funciona", gold: false },
                { icon: MessageCircle, title: "Falar no WhatsApp", sub: "Atendimento e reservas na hora", href: "https://wa.me/5521975968405?text=Ol%C3%A1!%20Gostaria%20de%20saber%20mais%20sobre%20o%20VM%20Corporate", gold: true, external: true },
              ].map((b) => {
                const Icon = b.icon;
                const inner = (
                  <>
                    <span className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${b.gold ? "bg-[#0A1128]/15 text-[#0A1128]" : "bg-[#D4AF37]/15 text-[#D4AF37]"}`}>
                      <Icon size={18} />
                    </span>
                    <span className="flex-1 min-w-0 text-left">
                      <span className={`block text-base font-bold ${b.gold ? "text-[#0A1128]" : "text-white"}`}>{b.title}</span>
                      <span className={`block text-xs ${b.gold ? "text-[#0A1128]/70" : "text-white/55"}`}>{b.sub}</span>
                    </span>
                    <ArrowRight size={18} className={b.gold ? "text-[#0A1128]" : "text-[#D4AF37]"} />
                  </>
                );
                const cls = `flex items-center gap-4 w-full rounded-2xl px-5 py-4 border transition-all hover:translate-x-1 ${
                  b.gold
                    ? "bg-[#D4AF37] border-[#D4AF37] shadow-xl shadow-[#D4AF37]/25"
                    : "bg-white/[0.06] border-white/15 backdrop-blur-md hover:bg-white/[0.1] hover:border-[#D4AF37]/50"
                }`;
                const tid = `hub-${b.title.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-")}`;
                if (b.route) return <Link key={b.title} to={b.href} className={cls} data-testid={tid}>{inner}</Link>;
                return (
                  <a key={b.title} href={b.href} className={cls} data-testid={tid}
                     {...(b.external ? { target: "_blank", rel: "noreferrer" } : {})}>
                    {inner}
                  </a>
                );
              })}
            </div>

            <div className="mt-16 grid grid-cols-3 gap-8 max-w-xl">
              {[
                { num: "200+", label: "Empresas atendidas" },
                { num: "6 dias", label: "Funcionamento semanal" },
                { num: "9", label: "Ambientes premium" },
              ].map((s) => (
                <div key={s.label}>
                  <div className="font-display text-3xl text-[#D4AF37] font-light">{s.num}</div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-white/50 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COMO FUNCIONA — 3 passos da jornada de reserva */}
      <section id="como-funciona" className="bg-white py-20 scroll-mt-24" data-testid="como-funciona-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-bold mb-3">Como funciona</div>
            <h2 className="font-display text-4xl lg:text-5xl text-[#0A1128] font-light leading-tight">
              O que é e como funciona o <span className="italic text-[#D4AF37]">VM Corporate</span>?
            </h2>
            <p className="text-gray-600 mt-5 leading-relaxed">
              É o aluguel de salas de trabalho e atendimento totalmente prontas e equipadas.
              Você não paga aluguel mensal fixo, condomínio, luz ou internet.
              Você reserva pelo tempo exato que precisa e paga somente por esse período.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-10 relative">
            {/* connector line for desktop */}
            <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-[2px] bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" aria-hidden />

            {[
              {
                n: "1", title: "Escolha o espaço",
                desc: "Mesa de Coworking, Escritório Executivo, Consultório ou Sala de Reunião.",
                icon: Building2,
              },
              {
                n: "2", title: "Reserve a hora",
                desc: "Pelo site ou direto no WhatsApp.",
                icon: Calendar,
              },
              {
                n: "3", title: "Chegue e use",
                desc: "Ambiente climatizado e recepção pronta para você e seus clientes.",
                icon: CheckCircle2,
              },
            ].map((step) => {
              const Icon = step.icon;
              return (
                <div key={step.n} className="relative text-center" data-testid={`step-${step.n}`}>
                  <div className="relative inline-block">
                    <div className="w-24 h-24 mx-auto rounded-full bg-[#0A1128] flex items-center justify-center shadow-xl shadow-[#0A1128]/20 mb-5">
                      <Icon size={32} className="text-[#D4AF37]" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-10 h-10 rounded-full bg-[#D4AF37] text-[#0A1128] flex items-center justify-center font-display text-xl font-bold border-4 border-white">
                      {step.n}
                    </div>
                  </div>
                  <h3 className="font-display text-xl text-[#0A1128] mb-2 mt-1">{step.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-xs mx-auto">{step.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-14">
            <Link
              to="/espacos"
              className="btn-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-md text-sm shadow-xl shadow-[#D4AF37]/30"
              data-testid="como-funciona-cta"
            >
              <Calendar size={16} /> Começar minha reserva <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* NOSSOS PREÇOS & ESPAÇOS — dynamic from /api/public/spaces, grouped by category */}
      <section id="precos" className="bg-[#0A1128] py-24 border-t border-white/5" data-testid="ambientes-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-4">Nossos Preços</div>
            <h2 className="font-display text-4xl lg:text-5xl text-white font-light leading-tight">
              Nossos Preços &
              <span className="italic text-[#D4AF37]"> Espaços.</span>
            </h2>
            <p className="text-white/60 mt-5 text-base leading-relaxed">
              Valores por hora, sem taxa escondida. Escolha o ambiente ideal e pague
              somente pelo tempo que utilizar.
            </p>
          </div>

          {ambientes.length === 0 ? (
            <div className="card-marketing p-12 text-center text-white/50" data-testid="ambientes-empty">
              Em breve, fotos dos nossos ambientes serão exibidas aqui.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="ambientes-grid">
              {/* DESTAQUE — Escritório Virtual (CNPJ) */}
              <div
                id="escritorio-virtual"
                className="relative overflow-hidden rounded-lg border-2 border-[#D4AF37]/70 bg-gradient-to-br from-[#14213D] to-[#0A1128] flex flex-col scroll-mt-24"
                data-testid="ambiente-card-escritorio-virtual"
              >
                <div className="absolute top-4 right-4 z-10 inline-flex items-center gap-1 px-3 py-1 bg-[#D4AF37] text-[#0A1128] text-[10px] uppercase tracking-[0.15em] font-bold rounded-full">
                  <Star size={10} /> Mais Vendido · CNPJ
                </div>
                <div className="p-6 pt-16 flex-1 flex flex-col">
                  <div className="font-display text-2xl text-white leading-tight mb-2">Escritório Virtual</div>
                  <p className="text-sm text-white/70 leading-relaxed mb-5">
                    Tenha a sua empresa no melhor endereço comercial do centro de Piabetá.
                  </p>
                  <ul className="space-y-2.5 mb-6">
                    {[
                      "Endereço Fiscal e Comercial para CNPJ",
                      "Gestão de Correspondência via WhatsApp",
                      "Imagem Profissional sem pagar aluguel de sala física",
                    ].map((b) => (
                      <li key={b} className="flex gap-2 text-sm text-white/80">
                        <CheckCircle2 size={16} className="text-[#D4AF37] flex-shrink-0 mt-0.5" /> {b}
                      </li>
                    ))}
                  </ul>
                  <div className="flex-1" />
                  <div className="font-display text-xl text-[#D4AF37] mb-4">A partir de R$ 49,00/mês</div>
                  <Link
                    to="/planos"
                    className="btn-gold inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-xs uppercase tracking-wider font-bold"
                    data-testid="virtual-office-cta"
                  >
                    Quero meu endereço <ArrowRight size={13} />
                  </Link>
                </div>
              </div>
              {ambientes.map((amb) => (
                <Link
                  to={`/espacos/${amb.sample.id}`}
                  key={amb.category}
                  className="group relative overflow-hidden rounded-lg border border-white/10 hover:border-[#D4AF37]/60 transition bg-[#0E1633]"
                  data-testid={`ambiente-card-${amb.category.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-[#14213D] to-[#0A1128]">
                    {amb.photo ? (
                      <img
                        src={amb.photo}
                        alt={amb.category}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-white/30">
                        <ImageIcon size={40} className="mb-3" />
                        <span className="text-xs uppercase tracking-[0.2em]">Foto em breve</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128] via-[#0A1128]/30 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 mb-1 font-bold">
                        {amb.count} {amb.count === 1 ? "ambiente disponível" : "ambientes disponíveis"}
                      </div>
                      <div className="font-display text-2xl text-white leading-tight">{amb.category}</div>
                      <div className="font-display text-xl text-[#D4AF37] mt-1" data-testid={`price-${amb.category.replace(/\s+/g, "-").toLowerCase()}`}>
                        {amb.category.toLowerCase().includes("cowork") ? "" : "A partir de "}
                        {formatBRL(amb.minRate)}/h
                      </div>
                    </div>
                  </div>
                  <div className="p-5 border-t border-white/5">
                    <p className="text-sm text-white/60 leading-relaxed mb-4 min-h-[3rem]">
                      {CATEGORY_COPY[amb.category] || amb.sample?.description || "Espaço pronto para receber você."}
                    </p>
                    <div className="inline-flex items-center gap-1.5 text-xs text-[#D4AF37] font-semibold uppercase tracking-[0.12em] group-hover:gap-2.5 transition-all">
                      Ver detalhes <ArrowRight size={13} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link
              to="/espacos"
              className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-3 rounded-md text-sm"
              data-testid="ambientes-cta-all"
            >
              Ver todos os ambientes <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="bg-[#0A1128] py-24" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-16">
            <div className="lg:col-span-1">
              <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-4">Diferenciais</div>
              <h2 className="font-display text-4xl lg:text-5xl text-white font-light leading-tight">
                Infraestrutura premium
                <span className="block italic text-[#D4AF37]">para resultados extraordinários.</span>
              </h2>
            </div>
            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {features.map((f) => (
                <div key={f.title} className="card-marketing p-6" data-testid={`feature-${f.title.split(" ")[0]}`}>
                  <div className="w-10 h-10 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mb-4">
                    <f.icon size={18} className="text-[#D4AF37]" />
                  </div>
                  <div className="font-display text-white text-lg mb-1">{f.title}</div>
                  <div className="text-sm text-white/60 leading-relaxed">{f.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROVA SOCIAL — empresas atendidas (placeholder visual sem logos fictícios) */}
      <section className="bg-white py-20" data-testid="prova-social-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-bold mb-3">Quem confia em nós</div>
            <h2 className="font-display text-3xl lg:text-4xl text-[#0A1128] font-light leading-tight">
              <span className="italic text-[#D4AF37]">Várias empresas</span>
              <span className="block">confiam em nossos serviços.</span>
            </h2>
            <p className="text-gray-600 mt-4 text-sm">
              Em breve compartilharemos cases reais. Por enquanto, conheça nossos números.
            </p>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-6 max-w-3xl mx-auto mb-14">
            {[
              { n: "9", l: "Ambientes premium" },
              { n: "Seg–Sáb", l: "Dias de funcionamento" },
              { n: "3 min", l: "Tempo médio para reservar" },
            ].map((s) => (
              <div key={s.l} className="text-center" data-testid={`stat-${s.l.split(" ")[0]}`}>
                <div className="font-display text-4xl lg:text-5xl text-[#0A1128] font-light leading-none">{s.n}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mt-2 font-bold">{s.l}</div>
              </div>
            ))}
          </div>

          {/* Logos / placeholder — área pronta para fotos reais futuras */}
          <div className="border-t border-gray-200 pt-10">
            <div className="text-center text-xs uppercase tracking-[0.2em] text-gray-400 font-bold mb-6">
              Empresas e profissionais clientes
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 opacity-60">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  className="h-16 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center text-[10px] uppercase tracking-widest text-gray-400 font-bold"
                  data-testid={`logo-placeholder-${i}`}
                >
                  Logo {i}
                </div>
              ))}
            </div>
            <div className="text-center text-[11px] text-gray-400 mt-4 italic">
              Em breve: logos reais dos nossos clientes
            </div>
          </div>
        </div>
      </section>

      {/* PLANS PREVIEW */}
      <section className="bg-[#0A1128] pb-24" data-testid="plans-preview">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-4">Banco de Horas</div>
            <h2 className="font-display text-4xl lg:text-5xl text-white font-light">
              Compre horas e utilize <span className="italic text-[#D4AF37]">quando precisar</span>.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(() => {
              // Curated selection: 1 destaque + Pacote 64h + Pacote 96h Coworking
              const day = plans.find(p => p.type === "day_use");
              const pkg64 = plans.find(p => p.category === "Coworking" && p.package_hours === 64);
              const pkg96 = plans.find(p => p.category === "Coworking" && p.package_hours === 96);
              const fallback = plans.filter(p => p.highlight).concat(plans);
              const curated = [day, pkg64, pkg96].filter(Boolean);
              const final = curated.length >= 3 ? curated : [...curated, ...fallback.filter(p => !curated.find(c => c?.id === p.id))].slice(0, 3);
              return final.slice(0, 3).map((p) => {
                const isPkg = p.pricing_mode === "package" && p.package_hours;
                return (
                  <div key={p.id} className={`card-marketing p-8 ${p.highlight || isPkg ? "border-[#D4AF37]/60 bg-[#D4AF37]/5" : ""}`} data-testid={`plan-preview-${p.id}`}>
                    {(p.highlight || isPkg) && (
                      <div className="inline-flex items-center gap-1 mb-4 px-3 py-1 bg-[#D4AF37] text-[#0A1128] text-[10px] uppercase tracking-[0.18em] font-bold rounded-full">
                        <Star size={11} /> {isPkg ? `Pacote ${p.package_hours}h` : "Mais escolhido"}
                      </div>
                    )}
                    <div className="font-display text-2xl text-white mb-2">{p.name}</div>
                    <div className="text-sm text-white/60 mb-6 leading-relaxed">{p.description}</div>
                    <div className="mb-6">
                      {isPkg ? (
                        <>
                          <div className="flex items-baseline gap-1">
                            <span className="font-display text-4xl text-[#D4AF37]">{formatBRL(p.price_package)}</span>
                            <span className="text-sm text-white/50">/{p.package_hours}h</span>
                          </div>
                          <div className="text-[11px] text-white/50 mt-1">Validade {p.validity_days || 30} dias · ~{((p.package_hours || 0) / 4).toFixed(0)}h por semana</div>
                        </>
                      ) : (
                        <>
                          <span className="text-xs text-white/50">a partir de</span>
                          <div className="flex items-baseline gap-1">
                            <span className="font-display text-4xl text-[#D4AF37]">{formatBRL(p.price_monthly || p.price_daily)}</span>
                            <span className="text-sm text-white/50">{p.price_monthly ? "/mês" : "/dia"}</span>
                          </div>
                        </>
                      )}
                    </div>
                    <ul className="space-y-2 mb-8">
                      {(p.features || []).slice(0, 4).map((f, i) => (
                        <li key={i} className="flex gap-2 text-sm text-white/70">
                          <CheckCircle2 size={16} className="text-[#D4AF37] flex-shrink-0 mt-0.5" /> {f}
                        </li>
                      ))}
                    </ul>
                    <Link to="/planos" className="btn-ghost-gold block text-center px-5 py-2.5 rounded-md text-sm">
                      Ver detalhes
                    </Link>
                  </div>
                );
              });
            })()}
          </div>
          <div className="text-center mt-12">
            <Link to="/planos" className="btn-gold inline-flex items-center gap-2 px-7 py-3 rounded-md text-sm">
              Ver Banco de Horas <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — placeholder visual structure (sem textos fictícios) */}
      <section className="bg-[#0A1128] py-24 border-t border-white/5" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-4">Quem confia em nós</div>
            <h2 className="font-display text-4xl lg:text-5xl text-white font-light leading-tight">
              Empresas e profissionais que confiam
              <span className="block italic text-[#D4AF37]">no VM Corporate.</span>
            </h2>
            <p className="text-white/60 mt-5">
              Em breve compartilharemos a voz de quem já faz parte da nossa comunidade.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="card-marketing p-7 flex flex-col"
                data-testid={`testimonial-placeholder-${i}`}
              >
                <Quote size={28} className="text-[#D4AF37]/40 mb-4" />
                <div className="flex-1 text-white/40 italic leading-relaxed text-sm min-h-[5rem]">
                  Depoimento em breve.
                </div>
                <div className="mt-6 flex items-center gap-3 pt-5 border-t border-white/5">
                  <div className="w-11 h-11 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                    <Users size={16} className="text-white/30" />
                  </div>
                  <div className="flex-1">
                    <div className="h-2.5 w-24 bg-white/10 rounded mb-2" />
                    <div className="h-2 w-16 bg-white/5 rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL — alinhado à nova jornada (Reservar primário + Tour/WhatsApp secundários) */}
      <section className="bg-[#0A1128] py-24 border-t border-white/5" data-testid="cta-final-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-display text-4xl lg:text-5xl text-white font-light leading-tight mb-6">
            Pronto para escolher seu
            <span className="italic text-[#D4AF37]"> ambiente?</span>
          </h2>
          <p className="text-lg text-white/60 mb-10 max-w-2xl mx-auto">
            Reserve online em menos de 3 minutos. Sem fidelidade, sem visita obrigatória.
            Se preferir conhecer antes, agende um tour gratuito ou fale com a equipe.
          </p>
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
            <Link
              to="/espacos"
              className="btn-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-md text-sm shadow-2xl shadow-[#D4AF37]/30"
              data-testid="bottom-cta-espacos"
            >
              <Calendar size={16} /> Conhecer espaços <ArrowRight size={14} />
            </Link>
            <a
              href="#tour-form"
              className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-md text-sm"
              data-testid="bottom-cta-tour"
            >
              Agendar tour gratuito
            </a>
            <a
              href="https://wa.me/5521975968405?text=Olá!%20Gostaria%20de%20saber%20mais%20sobre%20o%20VM%20Corporate"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3.5 rounded-md text-sm text-emerald-400 hover:text-emerald-300 hover:bg-emerald-400/5 transition-all"
              data-testid="bottom-cta-whatsapp"
            >
              <MessageCircle size={16} /> WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* TOUR / AGENDAMENTO DE VISITA — reposicionado para o final da página */}
      <section className="bg-[#0D1530] py-24 border-t border-white/5" data-testid="tour-section">
        <div className="max-w-xl mx-auto px-6">
          <form
            id="tour-form"
            onSubmit={submitTour}
            className="card-marketing p-7 tracing-line backdrop-blur-md bg-[#0A1128]/80"
            data-testid="hero-tour-form"
          >
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-bold mb-2">Tour 100% gratuito</div>
            <div className="font-display text-2xl text-white mb-1">Agende uma visita</div>
            <div className="text-sm text-white/60 mb-5">Te chamamos no WhatsApp em até 1h útil.</div>
            <div className="space-y-3">
              <input
                required
                placeholder="Seu nome *"
                value={tour.name}
                onChange={(e) => setTour({ ...tour, name: e.target.value })}
                className="w-full bg-white/5 border border-white/15 rounded-md px-4 py-3 text-white placeholder-white/40 focus:border-[#D4AF37] outline-none text-sm"
                data-testid="hero-tour-name"
              />
              <input
                required
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="WhatsApp *"
                value={tour.phone}
                onChange={(e) => setTour({ ...tour, phone: maskPhoneBR(e.target.value) })}
                className="w-full bg-white/5 border border-white/15 rounded-md px-4 py-3 text-white placeholder-white/40 focus:border-[#D4AF37] outline-none text-sm"
                data-testid="hero-tour-phone"
              />
              <input
                type="email"
                placeholder="E-mail (opcional)"
                value={tour.email}
                onChange={(e) => setTour({ ...tour, email: e.target.value })}
                className="w-full bg-white/5 border border-white/15 rounded-md px-4 py-3 text-white placeholder-white/40 focus:border-[#D4AF37] outline-none text-sm"
                data-testid="hero-tour-email"
              />
              <button
                type="submit"
                disabled={tourLoading}
                className="btn-gold w-full py-3 rounded-md text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="hero-tour-submit"
              >
                {tourLoading ? "Enviando…" : <>Agendar meu tour <ArrowRight size={14} /></>}
              </button>
            </div>
            <div className="text-[11px] text-white/40 text-center mt-3">
              Ao enviar, você aceita ser contactado pela nossa equipe.
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
